import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { api, apiError } from "../lib/api";
import { PageHeader, Card, Btn, Empty, Table, Td } from "../components/UI";
import { FLabel, FInput, FSelect, FTextarea } from "./engagement/FormControls";
import { DateField } from "./engagement/DateField";
import { SlidePanel, ConfirmDialog } from "../components/SlidePanel";
import { useClients, ClientSelect } from "./clinical/shared";
import { usePagination, PaginationBar } from "../components/Pagination";
import { Plus, Pencil, Trash2, Activity, TrendingUp, AlertTriangle, Tag } from "lucide-react";
import { toast } from "sonner";
import { ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";

const COCOA = ["#4E342E", "#8D6E63", "#BCAAA4", "#FFB300", "#2E7D32", "#5E35B1", "#1565C0", "#C62828", "#00838F", "#6D4C41"];
const SEV_TONE = { 1: "bg-[#E8F5E9] text-[#2E7D32]", 2: "bg-[#FFF8E1] text-[#8D6E00]", 3: "bg-[#FFF3E0] text-[#B84A00]", 4: "bg-[#FFEBEE] text-[#C62828]", 5: "bg-[#C62828] text-white" };
const EMPTY = { client_id: "", date: dayjs().format("YYYY-MM-DD"), time: "", behavior: "", category: "", severity: "1", antecedent: "", consequence: "", intervention: "", location: "", duration_minutes: "" };

function InsightsStrip({ rows, activeTrigger, onTriggerClick }) {
  const today = dayjs();
  const weekStart = today.subtract(6, "day").startOf("day");
  const prevWeekStart = today.subtract(13, "day").startOf("day");

  const thisWeek = rows.filter((r) => dayjs(r.date).isAfter(weekStart.subtract(1, "second")));
  const prevWeek = rows.filter((r) => {
    const d = dayjs(r.date);
    return d.isAfter(prevWeekStart.subtract(1, "second")) && d.isBefore(weekStart);
  });

  const trendPct = prevWeek.length ? Math.round(((thisWeek.length - prevWeek.length) / prevWeek.length) * 100)
    : (thisWeek.length ? 100 : 0);
  const trendUp = trendPct >= 0;

  // Weekly bars (last 7 days)
  const weekBars = [];
  for (let i = 6; i >= 0; i--) {
    const d = today.subtract(i, "day");
    const label = d.format("dd").slice(0, 2);
    const count = rows.filter((r) => dayjs(r.date).isSame(d, "day")).length;
    weekBars.push({ day: label, date: d.format("MMM D"), count });
  }

  // Most common trigger (antecedent) in this week
  const antecedents = {};
  thisWeek.forEach((r) => {
    const t = (r.antecedent || "").trim();
    if (t) antecedents[t] = (antecedents[t] || 0) + 1;
  });
  const topTrigger = Object.entries(antecedents).sort((a, b) => b[1] - a[1])[0];

  // Severity distribution (last 30 days)
  const monthStart = today.subtract(29, "day").startOf("day");
  const monthRows = rows.filter((r) => dayjs(r.date).isAfter(monthStart.subtract(1, "second")));
  const sevDist = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  monthRows.forEach((r) => { sevDist[r.severity] = (sevDist[r.severity] || 0) + 1; });
  const sevData = Object.entries(sevDist).map(([sev, count]) => ({ sev: `Sev ${sev}`, count }))
    .filter((x) => x.count > 0);

  const isTriggerActive = (t) => activeTrigger === t;
  const triggerBtnClass = (t) =>
    `rounded-full px-2 py-0.5 text-[10px] font-bold transition-colors ${isTriggerActive(t)
      ? "bg-[#4E342E] text-white ring-2 ring-offset-1 ring-[#4E342E]"
      : "bg-[#EDE7DC] text-[#4E342E] hover:bg-[#DBCDB6]"}`;

  return (
    <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3" data-testid="behavior-insights-strip">
      <Card className="p-4" data-testid="behavior-insights-trend">
        <div className="mb-2 flex items-center justify-between">
          <div className="text-[10px] font-bold uppercase text-[#7B6157]">Weekly Trend</div>
          <div className={`flex items-center gap-1 text-xs font-bold ${trendUp ? "text-[#C62828]" : "text-[#2E7D32]"}`}>
            <TrendingUp size={13} className={trendUp ? "" : "rotate-180"} />
            {thisWeek.length} this week ({trendPct >= 0 ? "+" : ""}{trendPct}%)
          </div>
        </div>
        <div className="h-20">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={weekBars} margin={{ top: 2, right: 2, left: 0, bottom: 0 }}>
              <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#8D6E63" }} />
              <YAxis hide />
              <Tooltip formatter={(v, _n, p) => [`${v} incident${v === 1 ? "" : "s"}`, p.payload.date]} labelFormatter={() => ""} />
              <Bar dataKey="count" fill="#4E342E" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-4" data-testid="behavior-insights-trigger">
        <div className="text-[10px] font-bold uppercase text-[#7B6157]">Most Common Trigger (7d)</div>
        {topTrigger ? (
          <div className="mt-2">
            <button type="button" onClick={() => onTriggerClick?.(topTrigger[0])}
              data-testid="behavior-top-trigger-btn"
              title={isTriggerActive(topTrigger[0])
                ? "Currently filtering by this trigger. Click again to clear."
                : "Filter incidents by this trigger"}
              className={`flex w-full items-start gap-2 rounded-lg p-1 text-left transition-colors ${isTriggerActive(topTrigger[0]) ? "bg-[#FFF3E0]" : "hover:bg-[#F5F2EB]"}`}>
              <Tag size={14} className="mt-0.5 text-[#8D5A00]" />
              <div>
                <div className="font-heading text-base font-bold text-[#3E2723]">{topTrigger[0]}</div>
                <div className="text-xs text-[#7B6157]">{topTrigger[1]} of {thisWeek.length} incident{thisWeek.length === 1 ? "" : "s"} this week</div>
              </div>
            </button>
            <div className="mt-3 flex flex-wrap gap-1">
              {Object.entries(antecedents).slice(0, 5).map(([t, c]) => (
                <button key={t} type="button" onClick={() => onTriggerClick?.(t)}
                  data-testid={`behavior-trigger-pill-${t.replace(/\s+/g, "-").toLowerCase().slice(0, 40)}`}
                  title={isTriggerActive(t) ? "Click again to clear the filter" : "Filter by this trigger"}
                  className={triggerBtnClass(t)}>
                  {t} ({c})
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="mt-2 text-xs italic text-[#7B6157]">No antecedents recorded this week</div>
        )}
      </Card>

      <Card className="p-4" data-testid="behavior-insights-severity">
        <div className="mb-2 flex items-center justify-between">
          <div className="text-[10px] font-bold uppercase text-[#7B6157]">Severity Distribution (30d)</div>
          <div className="flex items-center gap-1 text-xs font-bold text-[#5D4037]">
            <AlertTriangle size={13} className="text-[#8D5A00]" /> {(sevDist[4] || 0) + (sevDist[5] || 0)} high
          </div>
        </div>
        {sevData.length === 0 ? (
          <div className="text-xs italic text-[#7B6157]">No incidents in the last 30 days</div>
        ) : (
          <div className="h-24">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={sevData} dataKey="count" nameKey="sev" innerRadius={22} outerRadius={40} paddingAngle={2}>
                  {sevData.map((_, i) => <Cell key={i} fill={["#2E7D32", "#FFB300", "#E65100", "#C62828", "#7B0000"][i]} />)}
                </Pie>
                <Tooltip />
                <Legend verticalAlign="middle" align="right" layout="vertical" wrapperStyle={{ fontSize: 10 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>
    </div>
  );
}

function EntryForm({ open, onClose, categories, clients, editRow, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setForm(editRow ? { ...EMPTY, ...editRow, severity: String(editRow.severity), duration_minutes: String(editRow.duration_minutes || "") } : { ...EMPTY });
  }, [open, editRow]);
  const f = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const save = async () => {
    if (!form.client_id) return toast.error("Select a client");
    if (!form.behavior.trim()) return toast.error("Describe the behavior");
    setBusy(true);
    try {
      if (editRow) await api.put(`/behavior-tracker/${editRow.id}`, form);
      else await api.post("/behavior-tracker", form);
      toast.success(editRow ? "Entry updated" : "Behavior logged");
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <SlidePanel open={open} onClose={onClose} title={editRow ? "Edit Behavior Entry" : "Log Behavior"} width="max-w-2xl" testId="behavior-form-panel">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div><FLabel>Client *</FLabel><ClientSelect clients={clients} value={form.client_id} onChange={(v) => setForm((s) => ({ ...s, client_id: v }))} testId="bt-client-select" disabled={!!editRow} /></div>
          <div><FLabel>Category</FLabel>
            <FSelect data-testid="bt-category-select" value={form.category} onChange={f("category")}>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </FSelect></div>
          <div><FLabel>Date</FLabel><DateField testId="bt-date" value={form.date} onChange={(v) => setForm((s) => ({ ...s, date: v }))} noFuture /></div>
          <div><FLabel>Time</FLabel><FInput data-testid="bt-time" type="time" value={form.time} onChange={f("time")} /></div>
          <div><FLabel>Severity (1 to 5)</FLabel>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((s) => (
                <button key={s} type="button" data-testid={`bt-severity-${s}`} onClick={() => setForm((st) => ({ ...st, severity: String(s) }))}
                  className={`h-10 w-10 rounded-xl text-sm font-bold transition-colors ${form.severity === String(s) ? "bg-[#4E342E] text-white" : "bg-[#F5F2EB] text-[#6D4C41] hover:bg-[#EDE7DC]"}`}>{s}</button>
              ))}
            </div></div>
          <div><FLabel>Duration (minutes)</FLabel><FInput data-testid="bt-duration" type="number" min="0" value={form.duration_minutes} onChange={f("duration_minutes")} /></div>
        </div>
        <div><FLabel>Behavior observed *</FLabel><FTextarea data-testid="bt-behavior" rows={2} value={form.behavior} onChange={f("behavior")} /></div>
        <div className="grid grid-cols-2 gap-4">
          <div><FLabel>Antecedent (what happened before)</FLabel><FTextarea data-testid="bt-antecedent" rows={2} value={form.antecedent} onChange={f("antecedent")} /></div>
          <div><FLabel>Consequence (what happened after)</FLabel><FTextarea data-testid="bt-consequence" rows={2} value={form.consequence} onChange={f("consequence")} /></div>
        </div>
        <div><FLabel>Intervention applied</FLabel><FTextarea data-testid="bt-intervention" rows={2} value={form.intervention} onChange={f("intervention")} /></div>
        <div><FLabel>Location</FLabel><FInput data-testid="bt-location" value={form.location} onChange={f("location")} /></div>
        <div className="flex justify-end gap-2">
          <Btn variant="secondary" onClick={onClose} data-testid="bt-cancel-btn">Cancel</Btn>
          <Btn onClick={save} disabled={busy} data-testid="bt-save-btn">{busy ? "Saving…" : editRow ? "Update Entry" : "Log Behavior"}</Btn>
        </div>
      </div>
    </SlidePanel>
  );
}

function SummarySection({ clientId, clients }) {
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    if (!clientId) { setSummary(null); return; }
    api.get(`/behavior-tracker/summary/${clientId}`).then((r) => setSummary(r.data)).catch(() => {});
  }, [clientId]);
  if (!clientId || !summary) return null;
  const client = clients.find((c) => c.id === clientId);
  if (!summary.total) return <Card className="mb-4 p-5 text-sm text-[#84675B]" data-testid="bt-summary-empty">No behavior entries yet for {client?.first_name} {client?.last_name}.</Card>;
  return (
    <div className="mb-5 grid gap-4 lg:grid-cols-2" data-testid="bt-summary">
      <Card className="p-5">
        <h3 className="mb-3 flex items-center gap-2 font-heading text-sm font-bold text-[#3E2723]"><Activity size={15} /> Incidents by month</h3>
        <div className="h-56">
          <ResponsiveContainer>
            <BarChart data={summary.by_month}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EFEBE0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip /><Bar dataKey="count" fill="#4E342E" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
      <Card className="p-5">
        <h3 className="mb-3 font-heading text-sm font-bold text-[#3E2723]">Category mix ({summary.total} total)</h3>
        <div className="h-56">
          <ResponsiveContainer>
            <PieChart>
              <Pie data={summary.by_category} dataKey="count" nameKey="category" innerRadius={45} outerRadius={80}>
                {summary.by_category.map((_, i) => <Cell key={i} fill={COCOA[i % COCOA.length]} />)}
              </Pie>
              <Tooltip /><Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
}

export default function BehaviorTracker() {
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filterClient, setFilterClient] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterAntecedent, setFilterAntecedent] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [delRow, setDelRow] = useState(null);
  const clients = useClients();

  const filteredRows = filterAntecedent
    ? rows.filter((r) => (r.antecedent || "").trim() === filterAntecedent)
    : rows;
  const pg = usePagination(filteredRows);

  const load = () => {
    const p = new URLSearchParams();
    if (filterClient) p.set("client_id", filterClient);
    if (filterCategory) p.set("category", filterCategory);
    api.get(`/behavior-tracker?${p}`).then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  };
  useEffect(() => { api.get("/behavior-tracker/categories").then((r) => setCategories(r.data)).catch(() => {}); }, []);
  useEffect(load, [filterClient, filterCategory]);

  const remove = async () => {
    try { await api.delete(`/behavior-tracker/${delRow.id}`); toast.success("Deleted"); setDelRow(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const handleTriggerClick = (trigger) => {
    setFilterAntecedent((cur) => cur === trigger ? "" : trigger);
    pg.setPage?.(1);
  };

  return (
    <div data-testid="behavior-tracker-page">
      <PageHeader title="Behavior Tracker" subtitle="ABC behavior logging with severity, interventions & frequency analytics"
        action={<Btn onClick={() => { setEditRow(null); setFormOpen(true); }} data-testid="bt-log-btn"><Plus size={15} /> Log Behavior</Btn>} />
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="w-56"><FLabel>Filter by client</FLabel><ClientSelect clients={clients} value={filterClient} onChange={setFilterClient} testId="bt-filter-client" /></div>
        <div className="w-56"><FLabel>Filter by category</FLabel>
          <FSelect data-testid="bt-filter-category" value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} placeholder="All categories">
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </FSelect></div>
      </div>
      <InsightsStrip rows={rows} activeTrigger={filterAntecedent} onTriggerClick={handleTriggerClick} />
      {filterAntecedent && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-[#B26A00] bg-[#FFF3E0] p-3"
          data-testid="behavior-trigger-active-filter">
          <Tag size={14} className="text-[#8D5A00]" />
          <span className="text-xs font-bold text-[#8D5A00]">Filtering incidents by trigger:</span>
          <span className="rounded-full bg-white px-2.5 py-0.5 text-xs font-bold text-[#4E342E]">{filterAntecedent}</span>
          <span className="text-[11px] text-[#7B6157]">{filteredRows.length} of {rows.length} incident{rows.length === 1 ? "" : "s"} shown</span>
          <button type="button" onClick={() => setFilterAntecedent("")}
            data-testid="behavior-trigger-clear"
            className="ml-auto inline-flex items-center gap-1 rounded-full bg-[#4E342E] px-2.5 py-0.5 text-[10px] font-bold text-white hover:bg-[#3E2723]">
            Clear filter
          </button>
        </div>
      )}
      <SummarySection clientId={filterClient} clients={clients} />
      <Card className="overflow-hidden" data-testid="bt-table">
        {filteredRows.length === 0 ? (
          <Empty text={filterAntecedent ? `No behavior entries with trigger "${filterAntecedent}".` : "No behavior entries yet."} />
        ) : (
          <Table headers={["Date", "Client", "Category", "Behavior", "Severity", "Intervention", "Recorded by", ""]}>
            {pg.pageRows.map((r) => (
              <tr key={r.id} className="border-t border-[#F1EEE7] hover:bg-[#FDFCF8]" data-testid={`bt-row-${r.id}`}>
                <Td>{dayjs(r.date).format("MM/DD/YYYY")}{r.time && <span className="text-xs text-[#84675B]"> {r.time}</span>}</Td>
                <Td className="font-medium">{r.client_name}</Td>
                <Td>{r.category}</Td>
                <Td className="max-w-[240px] truncate" title={r.behavior}>{r.behavior}</Td>
                <Td><span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${SEV_TONE[r.severity] || SEV_TONE[1]}`}>{r.severity}</span></Td>
                <Td className="max-w-[200px] truncate text-[#7B6157]" title={r.intervention}>{r.intervention || "-"}</Td>
                <Td className="text-[#7B6157]">{r.recorded_by}</Td>
                <Td>
                  <div className="flex gap-1">
                    <button data-testid={`bt-edit-${r.id}`} onClick={() => { setEditRow(r); setFormOpen(true); }} className="rounded-lg p-1.5 text-[#6D4C41] hover:bg-[#F5F2EB]"><Pencil size={15} /></button>
                    <button data-testid={`bt-delete-${r.id}`} onClick={() => setDelRow(r)} className="rounded-lg p-1.5 text-[#C62828] hover:bg-[#FFEBEE]"><Trash2 size={15} /></button>
                  </div>
                </Td>
              </tr>
            ))}
          </Table>
        )}
        <PaginationBar {...pg} testId="bt-pagination" />
      </Card>
      <EntryForm open={formOpen} onClose={() => setFormOpen(false)} categories={categories} clients={clients}
        editRow={editRow} onSaved={() => { setFormOpen(false); load(); }} />
      <ConfirmDialog open={!!delRow} onClose={() => setDelRow(null)} onConfirm={remove}
        description={`Delete this behavior entry for ${delRow?.client_name}?`} />
    </div>
  );
}
